import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { PlayerTimezoneSelect } from "@/pages/player/form/PlayerTimezoneSelect";
import { PlayerMediaSelect } from "@/pages/player/form/PlayerMediaSelect";
import { AreaTypeSelect } from "@/pages/areaType/form/AreaTypeSelect";
import { Switch } from "@/components/ui/switch";
import { AssignedContentList } from "@/components/form/assignedContentList/AssignedContentList";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";

const formSchema = z.object({
  name: z.string().min(1),
  areaTypeId: z.number(),
  timezone: z.string().min(1),
  overwriteSub: z.boolean(),
  defaultContentId: z.number().optional().or(z.null()),
  contentList: z.array(
    z.object({
      contentId: z.number().optional().or(z.null()),
      contentGroupId: z.number().optional().or(z.null()),
      id: z.string().optional(),
    })
  ),
});

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
  defaultValues?: FormSchemaType;
};

export const AreaMetaForm: React.FC<Props> = ({ buttonText, onSubmit, isLoading, defaultValues }) => {
  const { t } = useTranslation();
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      name: "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      overwriteSub: false,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("common.name")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("common.name")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="areaTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("entities.areaType")}</FormLabel>
                <FormControl>
                  <AreaTypeSelect field={field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("entities.timeZone")}</FormLabel>
                <FormControl>
                  <div>
                    <PlayerTimezoneSelect field={field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <PlayerMediaSelect
            label={t("content.defaultContent")}
            form={form}
            visibleTypes={["content"]}
            properties={{
              contentId: "defaultContentId",
            }}
          />
          <FormField
            control={form.control}
            name="overwriteSub"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  {t("common.overwriteSub")}{" "}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon className="w-4 h-4 text-destructive" />
                    </TooltipTrigger>
                    <TooltipContent>{t("common.overwriteSubDesc")}</TooltipContent>
                  </Tooltip>
                </FormLabel>
                <FormControl>
                  <div>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <AssignedContentList form={form as any} visibleTypes={["content", "contentGroup"]} />
        <Button type="submit" disabled={isLoading}>
          {buttonText}
          {isLoading && <LoadingSmall />}
        </Button>
      </form>
    </Form>
  );
};
